import { useParams, Link } from "react-router-dom";
import { services } from "../data";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import { motion } from "framer-motion";
import { FaArrowLeft, FaCheckCircle, FaRocket, FaShieldAlt, FaUsers, FaTools, FaBuilding } from "react-icons/fa";

function ServiceDetailsPage({ openModal }) {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="error-page">
        <h2>Service not found</h2>
        <Link to="/">Back to Home</Link>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <>
      <Navbar openModal={openModal} />
      
      <main className="service-detail-page">
        {/* ENHANCED HERO */}
        <header className="detail-hero">
          <div className="hero-glow"></div>
          <div className="container">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="breadcrumb"
            >
              <Link to="/"><FaArrowLeft /> Back to Services</Link>
            </motion.div>
            
            <div className="hero-grid">
              <div className="hero-text">
                <motion.span 
                  className="section-tag"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  Expertise in {service.title}
                </motion.span>
                <motion.h1 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                >
                  {service.title}
                </motion.h1>
                <motion.p 
                  className="lead"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {service.longDesc}
                </motion.p>
                <motion.div 
                  className="hero-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <button className="btn-primary" onClick={openModal}>Schedule a Consultation</button>
                </motion.div>
              </div>

              <motion.div 
                className="hero-image-vessel"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <div className="hero-img-glass">
                  <img src={service.heroImage} alt={service.title} />
                </div>
              </motion.div>
            </div>
          </div>
        </header>

        {/* COMPREHENSIVE SOLUTIONS */}
        <section className="detail-content solutions-section">
          <div className="container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-tag">Capabilities</span>
              <h2>Comprehensive Solutions</h2>
            </motion.div>
            
            <div className="detail-grid">
              <motion.div 
                className="features-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {service.features.map((feature, index) => (
                  <motion.div className="feature-item-glass" key={index} variants={itemVariants}>
                    <FaCheckCircle className="check-icon" />
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE US - NEW */}
        <section className="why-choose-section">
          <div className="container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-tag">Value Proposition</span>
              <h2>Why Choose Our Expertise?</h2>
            </motion.div>
            
            <div className="why-grid">
              {service.whyChoose.map((point, index) => {
                const Icons = [FaShieldAlt, FaUsers, FaTools, FaRocket];
                const Icon = Icons[index % Icons.length];
                return (
                  <motion.div 
                    className="why-card" 
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="why-icon-wrapper">
                      <Icon />
                    </div>
                    <p>{point}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* INTEGRATIONS - NEW */}
        <section className="integrations-section">
          <div className="container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-tag">Connectivity</span>
              <h2>Seamless Integrations</h2>
              <p>We connect Salesforce with your existing technology ecosystem.</p>
            </motion.div>
            
            <div className="integration-ticker">
              <div className="ticker-track">
                {service.integrations.concat(service.integrations).map((item, index) => (
                  <div className="ticker-item" key={index}>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* INDUSTRIES SERVED - NEW */}
        <section className="industries-section-dark">
          <div className="container">
            <div className="industry-flex">
              <motion.div 
                className="industry-text"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="section-tag">Impact</span>
                <h2>Industries We Serve</h2>
                <p>Tailored Salesforce solutions for sector-specific challenges.</p>
              </motion.div>
              <motion.div 
                className="industry-list"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                {service.industries.map((ind, index) => (
                  <div className="industry-tag" key={index}>
                    <FaBuilding /> {ind}
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* BUSINESS BENEFITS */}
        <section className="benefits-section-enhanced">
          <div className="container">
            <div className="benefits-grid-large">
              {service.benefits.map((benefit, index) => (
                <motion.div 
                  className="benefit-pill" 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <FaRocket className="rocket-icon" />
                  <span>{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <CTA openModal={openModal} />
      </main>

      <Footer />
    </>
  );
}

export default ServiceDetailsPage;
